import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { EventItem, Place } from '../../types/shared/event';
import { BookIcon, WalkIcon, BikeIcon } from '../icons';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { useDetailCardTranslation } from '../../hooks/useDetailCardTranslation';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { calculateAllTravelTimes } from '../../utils/travelTimeCalculator';
import { favoritesService } from '../../lib/favorites/favoritesService';
import { useSection } from '../../hooks/useSection';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime, formatTimeRange } from '../../utils/formatDuration';
import { ticketmasterEventAdapter } from '../../lib/adapters/ticketmasterEventAdapter';
import { getHotelItineraryUrl } from '../../utils/itineraryUtils';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';

interface DetailCardProps {
  item: EventItem;
  items: EventItem[];
  onClose: () => void;
  onItemChange: (item: EventItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
  behavior?: 'default' | 'pin' | 'place';
  showIndex?: number | null;
  place?: Place | null;
  context?: {
    translationTableId?: string;
    locale?: string;
  };
  onShowPlace?: (place: Place) => void;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors,
  behavior = 'default',
  showIndex,
  place,
  context,
  onShowPlace
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const [isFavorite, setIsFavorite] = useState(() => {
    const favoriteId = item.source === 'ticketmaster' ? `ticketmaster_${item.eventId}` : item.id;
    return favoritesService.isFavorite(favoriteId);
  });

  const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'detailCard');

  const travelTimes = React.useMemo(() => {
    if (typeof item.distance === 'number' && !isNaN(item.distance)) {
      return calculateAllTravelTimes(item.distance);
    }
    return { walkTime: 0, bikeTime: 0, driveTime: 0 };
  }, [item.distance]);

  const { getDescription, getOffer, translatingDescription, descriptionError } = useDetailCardTranslation({
    itemId: item.eventId || item.id,
    itemName: item.name,
    description: item.description,
    offer: item.offer,
    descriptions: item.descriptions,
    offers: item.offers,
    tableId: context?.translationTableId || tableId,
    sectionType: 'event-api'
  });

  useEffect(() => {
    const checkCategoryAndTypeTranslations = async () => {
      if (!context?.locale || item.source !== 'ticketmaster') return;

      try {
        if (item.category && !translations[item.category]) {
          const categoryTranslations = await ticketmasterEventAdapter.translateCategoryOrType(
            item.category,
            context.locale as any
          );
          if (categoryTranslations) {
            Object.keys(categoryTranslations).forEach(lang => {
              if (!translations[item.category]) translations[item.category] = {};
              translations[item.category][lang] = categoryTranslations[lang];
            });
          }
        }
        if (item.type && !translations[item.type]) {
          const typeTranslations = await ticketmasterEventAdapter.translateCategoryOrType(
            item.type,
            context.locale as any
          );
          if (typeTranslations) {
            Object.keys(typeTranslations).forEach(lang => {
              if (!translations[item.type]) translations[item.type] = {};
              translations[item.type][lang] = typeTranslations[lang];
            });
          }
        }
      } catch (error) {
        // silent
      }
    };

    checkCategoryAndTypeTranslations();
  }, [item, translations, context?.locale]);

  const otherDates = React.useMemo(() => {
    if (!item.showId) return [];
    return items
      .filter(event => event.showId === item.showId && event.id !== item.id)
      .sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        return dateCompare !== 0 ? dateCompare : a.hour.localeCompare(b.hour);
      });
  }, [items, item]);

  const handleFavoriteToggle = async (newState: boolean) => {
    try {
      const favoriteId = item.source === 'ticketmaster' ? `ticketmaster_${item.eventId}` : item.id;

      if (newState) {
        favoritesService.addFavorite({
          id: favoriteId,
          sourceType: 'event-api',
          data: { ...item, image: item.image },
          metadata: {
            sourceSection: section,
            sectionSlug: section.names.fr,
            sectionNames: section.names,
            item_id: item.id,
            section_id: section.id,
            source_hash: item.source === 'ticketmaster' ? item.description : undefined
          }
        });
        analyticsService.trackItemAction(
          section.id, item.id, 'add_favorite', 'event',
          { item_name: item.name, category: item.category, type: item.type, section_name: section.names.fr || 'Unknown Section', section_type: 'event-api' }
        );
      } else {
        favoritesService.removeFavorite(favoriteId);
        analyticsService.trackItemAction(
          section.id, item.id, 'remove_favorite', 'event',
          { item_name: item.name, category: item.category, type: item.type, section_name: section.names.fr || 'Unknown Section', section_type: 'event-api' }
        );
      }
      setIsFavorite(newState);
    } catch (error) {
      // silent
    }
  };

  const translate = (key: string) => getTranslatedText(translations, key, i18n.language);
  const translateCat = (key: string) => getTranslatedCategory(translations, key, i18n.language);

  const renderContent = (item: EventItem) => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      <div className="w-full">{item.place}</div>

      <div className="w-full">
        {item.fulladress && <span>{item.fulladress}</span>}
        {item.fulladress && item.telephone && <span> &bull; </span>}
        {item.telephone && <span>{formatPhoneNumber(item.telephone)}</span>}
      </div>

      <div className="flex flex-wrap gap-2">
        {item.distance && (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{item.distance}km</span>
          </div>
        )}
        {item.timewalk && (
          <div className="flex items-center">
            <WalkIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timewalk, i18n.language)}</span>
          </div>
        )}
        {item.timebike && (
          <div className="flex items-center">
            <BikeIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timebike, i18n.language)}</span>
          </div>
        )}
        {item.timedrive && (
          <div className="flex items-center">
            <Car size={15} className="mr-1" fill="currentColor" />
            <span>{formatTravelTime(item.timedrive, i18n.language)}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {item.date && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>
              {new Intl.DateTimeFormat(i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              }).format(new Date(item.date))}
              {item.hour && item.end_time ? (
                ` ${formatTimeRange(item.hour, item.end_time, i18n.language)}`
              ) : item.hour && item.duration ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))} (${formatTravelTime(item.duration, i18n.language)})`
              ) : item.hour ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))}`
              ) : ''}
            </span>
          </div>
        )}
        {item.price && (
          <div className="flex items-center" style={{ color: config['major-color'] }}>
            <Tag size={15} className="mr-1" />
            <span>{item.price}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingButton = (item: EventItem) => {
    if (!item.booking) return null;

    return (
      <a
        href={item.booking}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          analyticsService.trackItemAction(
            tableId, item.id, 'booking_click', 'event',
            { item_name: item.name, category: item.category, type: item.type, url: item.booking, section_name: section.names.fr || 'Unknown Section', section_type: 'event-api' }
          );
        }}
        className="flex items-center justify-center px-4 py-2 rounded-lg text-white mb-2"
        style={{ backgroundColor: config['major-color'] }}
      >
        <BookIcon size={18} className="mr-2" />
        {translate('btn_booking')}
      </a>
    );
  };

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={translateCat}
      getTranslatedText={translate}
      itineraryUrl={getHotelItineraryUrl(item.fulladress, config.fulladress)}
      behavior={behavior}
      showIndex={showIndex}
      place={place}
      context={{
        translationTableId: context?.translationTableId || tableId,
        sectionType: 'event-api',
        source_hash: item.source === 'ticketmaster' ? item.description : undefined,
        contentType: 'description'
      }}
      description={getDescription()}
      offer={getOffer()}
      translating={translatingDescription}
      translationError={descriptionError}
      otherDates={otherDates}
      onOtherDateClick={onItemChange}
      onShowPlace={onShowPlace}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
    />
  );
};

export default DetailCard;
